import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import Meta from '@bizapp-lp/workflow/organisms/Meta';
import { prefectureNameNameMap } from '@bizapp-lp/workflow/molecules/ZipcodeInput';
import { InputProps } from '@bizapp-lp/workflow/organisms/InputFormType';
import TwoColumnForm from '@bizapp-lp/workflow/templates/TwoColumnForm';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    lineBreaks: {
      display: 'block',
    },
  }),
);

export interface TrialApplicationProps {
  grecaptchaKey?: string;
  apiBaseUrl: string;
}

const title = 'Workflow | 株式会社ワークスアプリケーションズ・エンタープライズ';
const description =
  '株式会社ワークスアプリケーションズ・エンタープライズがご提供する、Workflow無料トライアルお申し込みページです。';

const TrialApplication: React.FC<TrialApplicationProps> = (props) => {
  const classes = useStyles();

  const inputList: InputProps[] = [
    {
      kind: 'sectionTitle',
      id: 'title-service',
      label: 'お申込み対象',
    },
    {
      id: 'service-product',
      label: '利用サービス',
      disabled: true,
      value: 'Workflow',
    },
    {
      style: { textAlign: 'right' },
      id: 'service-license-number',
      label: 'お申込みライセンス数',
      type: 'number',
      disabled: true,
      value: '20',
      logicOptions: {
        validationType: 'non-negative-integer',
      },
    },

    {
      kind: 'sectionTitle',
      id: 'title-organization',
      label: '会社・団体情報',
    },
    {
      id: 'organization-name',
      label: '企業・団体名',
      name: 'organization',
      required: true,
      logicOptions: {
        validationType: 'required',
      },
    },
    {
      id: 'organization-name-kana',
      label: '企業・団体名（フリガナ）',
      name: 'organization-kana',
    },
    {
      kind: 'zipcode',
      style: { size: 'short' },
      id: 'organization-postal-code',
      label: '郵便番号',
      name: 'postal-code',
      required: true,
      logicOptions: {
        validationType: 'zipcode',
        prefectureId: 'organization-prefecture',
        addressId: 'organization-address',
      },
    },
    {
      kind: 'select',
      style: { size: 'short' },
      id: 'organization-prefecture',
      label: '都道府県',
      name: 'prefecture',
      selectOptions: prefectureNameNameMap,
      required: true,
      logicOptions: {
        validationType: 'required',
      },
      value: '',
    },
    {
      kind: 'input',
      style: { size: 'long' },
      id: 'organization-address',
      label: '所在地',
      name: 'address',
      required: true,
      logicOptions: {
        validationType: 'required',
      },
      helperText: '住所の続きを入力してください。',
    },

    {
      kind: 'sectionTitle',
      id: 'title-contact-person',
      label: '担当者情報',
    },
    {
      id: 'contact-person-name',
      label: '氏名',
      name: 'name',
      required: true,
      logicOptions: {
        validationType: 'required',
      },
    },
    {
      id: 'contact-person-name-kana',
      label: '氏名（フリガナ）',
      name: 'name-kana',
    },
    {
      style: { size: 'long-area' },
      id: 'contact-person-title',
      label: '所属・役職',
      name: 'organization-title',
      required: true,
      logicOptions: {
        validationType: 'required',
      },
    },
    {
      style: { size: 'long-area' },
      id: 'contact-person-mail',
      label: 'メールアドレス',
      type: 'email',
      name: 'email',
      required: true,
      logicOptions: {
        validationType: 'mail',
      },
    },
    {
      id: 'contact-person-tel1',
      label: '電話番号',
      type: 'tel',
      name: 'tel',
      required: true,
      logicOptions: {
        validationType: 'tel',
      },
    },
    {
      id: 'contact-person-tel2',
      label: '携帯番号 (テレワーク中の方など連絡が取れる番号)',
      type: 'tel',
      name: 'tel',
      logicOptions: {
        validationType: 'tel',
      },
    },

    {
      kind: 'sectionTitle',
      id: 'title-registration',
      label: 'サービス登録情報',
      description: (
        <Typography variant="body2">
          {
            'サービス利用開始にあたって新規に発行される環境と初期管理者アカウントの情報を入力してください。'
          }
        </Typography>
      ),
    },
    {
      id: 'registration-name',
      label: '管理者アカウント 氏名',
      name: 'registration-name',
      required: true,
      logicOptions: {
        validationType: 'required',
      },
    },
    {
      id: 'registration-name-kana',
      label: '管理者アカウント 氏名（フリガナ）',
      name: 'registration-name-kana',
    },
    {
      style: { size: 'long-area' },
      id: 'registration-mail',
      label: '管理者アカウント メールアドレス',
      type: 'email',
      name: 'email',
      required: true,
      logicOptions: {
        validationType: 'mail',
      },
    },
    {
      style: { size: 'long-area' },
      id: 'registration-user-id',
      label: '管理者アカウント ユーザーID',
      name: 'user-id',
      autoCapitalize: 'off',
      autoCorrect: 'off',
      required: true,
      description: (
        <Typography>
          <span>
            ユーザーIDは、ログイン時に利用するユーザー固有の情報です。
          </span>
          <span>
            SAML認証等の利用を予定している場合は、ユーザーを識別する要素となる情報を指定してください。
          </span>
          <span>例）社員番号、ユーザーネームなど</span>
          <span>
            <strong>アルファベット</strong>、<strong>半角数字</strong>および記号
            <strong>「-_@.」</strong>のみが使用できます。
          </span>
        </Typography>
      ),
      logicOptions: {
        validationType: 'userId',
      },
    },
  ];

  return (
    <GoogleReCaptchaProvider reCaptchaKey={props.grecaptchaKey}>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <TwoColumnForm
        apiBaseUrl={props.apiBaseUrl}
        heading={'無料トライアルのお申し込み'}
        description={
          <>
            <span className={classes.lineBreaks}>
              下記フォームにご記入ください。
            </span>
            <span className={classes.lineBreaks}>
              フォーム送信後、弊社よりご案内をお送りいたします。
            </span>
          </>
        }
        inputList={inputList}
        submitButtonLabel="送信する"
        toUrl="/workflow/complete-trial-application"
      />
    </GoogleReCaptchaProvider>
  );
};

export default TrialApplication;
